/**
 * NEXT_PUBLIC_CONNECT_CATCHUP_V1, the web half of the connect catch-up: a page that comes back
 * (the browser is online again, the tab is shown) checks its link instead of believing the socket.
 *
 * A socket that died under a network change or a sleeping laptop keeps `connected` true until
 * Socket.IO's own timeout (ping interval plus ping timeout, 75 s) or the health rail's silence clock
 * (35 s); every frame of a turn that ends meanwhile is lost (staging 3 Oct, task 249f9fad). So on a
 * return the link must answer one heartbeat within HEARTBEAT_ACK_TIMEOUT_MS, or it is a deaf link
 * and re-dials; the reconnect brings the server's catch-up (one MESSAGE_SYNC per recently active
 * task) with it. A socket that already knows it is down re-dials as it always did.
 *
 * The flag is read at CALL time so tests can stub it and the off path stays exactly today's.
 */
import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { HEARTBEAT_ACK_TIMEOUT_MS } from "./connectionHealthStore";

export function isConnectCatchUpEnabled(): boolean {
  return process.env.NEXT_PUBLIC_CONNECT_CATCHUP_V1 === "true";
}

export interface LinkRecheckDeps {
  socket: Pick<Socket, "connected" | "timeout">;
  /** The socket knows it is down: re-dial (today's online / visible path). */
  reconnect: () => void;
  /** The socket says it is up but did not answer: re-dial a deaf link. */
  bounce: () => void;
}

/** The page came back: re-dial a socket that is down, and prove one that says it is up. */
export function recheckLink(deps: LinkRecheckDeps): void {
  if (!deps.socket.connected) {
    deps.reconnect();
    return;
  }
  if (!isConnectCatchUpEnabled()) return;
  deps.socket
    .timeout(HEARTBEAT_ACK_TIMEOUT_MS)
    .emit(EVENTS.CONNECTION_HEARTBEAT, {}, (err: unknown) => {
      // Only a link that is still believed up is bounced: one that dropped meanwhile re-dials itself.
      if (err && deps.socket.connected) deps.bounce();
    });
}
